import axios from "axios";
import { useCallback, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { AppStateContext } from "../context/AppContext";
import apiPath from "../constants/apiPath";

const client = axios.create({
  baseURL: apiPath.baseURL,
});

/** These return 401 for wrong credentials — must not trigger global logout + full page reload */
const isPublicAuthUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  const paths = [
    apiPath.adminLogin,
    apiPath.login,
    apiPath.authForgotPassword,
    apiPath.authVerifyOtp,
    apiPath.authResetPassword,
    "auth/login",
    "auth/signup",
    "auth/register",
  ].filter(Boolean);
  return paths.some((p) => url === p || url.endsWith(p) || url.includes(`/${p}`) || url.includes(p));
};

const useRequest = () => {
  const { logout } = useContext(AuthContext);
  const { setLoading } = useContext(AppStateContext);

  const request = useCallback(async ({
    url,
    method: tmethod,
    data,
    onSuccess,
    onError,
    header,
    responseType, // <-- add this
    rawResponse = false, // <-- add this
  }) => {
    const method = tmethod.trim().toUpperCase();
    const token = localStorage.getItem("token") || "";

    const headers = {
      ...header,
      Authorization: `Bearer ${token}`,
    };

    setLoading(true);
    try {
      const response = await client({
        url,
        method,
        data,
        headers,
        responseType, // <-- pass it to axios
      });

      setLoading(false);

      if (onSuccess) {
        if (rawResponse) {
          // return the full Axios response
          onSuccess(response);
        } else {
          onSuccess(response.data);
        }
      }

      return rawResponse ? response : response.data;
    } catch (err) {
      setLoading(false);

      if (onError) onError(err);

      const inactive =
        err?.response?.status === 403 &&
        err?.response?.data?.code === "ACCOUNT_INACTIVE";
      const deleted =
        err?.response?.status === 403 &&
        err?.response?.data?.code === "ACCOUNT_DELETED";

      if (inactive && !isPublicAuthUrl(url)) {
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        window.location.assign("/login?account=inactive");
        return null;
      }

      if (deleted && !isPublicAuthUrl(url)) {
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        window.location.assign("/login?account=deleted");
        return null;
      }

      if (err?.response?.status === 401 && !isPublicAuthUrl(url)) {
        logout();
      }

      return null;
    }
  }, [logout, setLoading]);

  return { request };
};

export default useRequest;
